import * as React from 'react'
import { compose, graphql } from 'react-apollo'
import { injectIntl, FormattedMessage } from 'react-intl'
import gql from 'graphql-tag'
import Button from 'web-makeup/dist/Button'
import Modal from 'web-makeup/dist/Modal'
import Input from 'web-makeup/dist/Input'
import { User } from 'web-fns/dist/auth'

import {
  RequestTicket,
  RequestTicketState,
  ModerationClaimRequestTicketMutationArgs,
  ModerationApproveRequestTicketMutationArgs,
  ModerationRejectRequestTicketMutationArgs,
  ModerationDropRequestTicketMutationArgs,
} from '../../../typings/types'
import { FullWidthButtonGroup } from '../RequestDetailsPage.style'
import { isModerator } from '../../../helpers/role-helper'

export const CLAIM_REQUEST_TICKET = gql`
  mutation moderationClaimRequestTicket($uuid: ID!) {
    moderationClaimRequestTicket(uuid: $uuid) {
      moderationClaimedAt
    }
  }
`
export const APPROVE_REQUEST_TICKET = gql`
  mutation moderationApproveRequestTicket($uuid: ID!) {
    moderationApproveRequestTicket(uuid: $uuid) {
      moderationClaimedAt
    }
  }
`
export const REJECT_REQUEST_TICKET = gql`
  mutation moderationRejectRequestTicket($uuid: ID!, $detail: String!) {
    moderationRejectRequestTicket(uuid: $uuid, detail: $detail) {
      moderationClaimedAt
    }
  }
`
export const DROP_REQUEST_TICKET = gql`
  mutation moderationDropRequestTicket($uuid: ID!) {
    moderationDropRequestTicket(uuid: $uuid) {
      moderationClaimedAt
    }
  }
`

const shouldShowModerationClaimButton = (requestTicket: RequestTicket) => {
  return isModerator() && requestTicket.state === RequestTicketState.RESOLVED
}
const shouldShowModerationPanel = (requestTicket: RequestTicket, user?: User) => {
  const ownTicket =
    requestTicket.moderator && user && requestTicket.moderator.id === user.id!.toString()
  return ownTicket && requestTicket.state === RequestTicketState.MODERATION_CLAIMED
}
const shouldShowTicketClaimedButton = (requestTicket: RequestTicket, user?: User) => {
  const ownTicket =
    requestTicket.moderator && user && requestTicket.moderator.id === user.id!.toString()
  return (
    isModerator() && requestTicket.state === RequestTicketState.MODERATION_CLAIMED && !ownTicket
  )
}

type MutationFunc<T> = (variables: { variables: T }) => Promise<void>
type Props = {
  requestTicket: RequestTicket
  user?: User
  refetchFunc: () => Promise<void>
  claimTicket: MutationFunc<ModerationClaimRequestTicketMutationArgs>
  approveTicket: MutationFunc<ModerationApproveRequestTicketMutationArgs>
  rejectTicket: MutationFunc<ModerationRejectRequestTicketMutationArgs>
  dropTicket: MutationFunc<ModerationDropRequestTicketMutationArgs>
} & ReactIntl.InjectedIntlProps

enum ModerationButton {
  CLAIM,
  APPROVE,
  REJECT,
  DROP,
}
interface State {
  loading: boolean
  loadingButton?: ModerationButton
  modalOpen: boolean
  rejectReason?: string
}

class ModerationPanel extends React.Component<Props, State> {
  constructor(props) {
    super(props)

    this.state = {
      loading: false,
      modalOpen: false,
    }
  }

  async rejectTicket() {
    const { rejectReason } = this.state
    const { requestTicket, rejectTicket, refetchFunc } = this.props

    this.setState({ loading: true, loadingButton: ModerationButton.REJECT, modalOpen: false })
    try {
      await rejectTicket({ variables: { uuid: requestTicket.uuid, detail: rejectReason || '' } })
      await refetchFunc()
    } catch (error) {
      this.setState({ loading: false })
    }
  }

  render() {
    const { requestTicket, user, refetchFunc, intl, ...mutations } = this.props
    const { claimTicket, approveTicket, dropTicket } = mutations

    const { loading, loadingButton, modalOpen, rejectReason } = this.state

    const rejectModal = (
      <Modal
        visible={modalOpen}
        title={intl.formatMessage({ id: 'request_details.moderation_message.reject_reason.title' })}
        onCancel={() => this.setState({ modalOpen: false })}
        onOk={() => this.rejectTicket()}
      >
        <Input.TextArea
          value={rejectReason}
          onChange={e => this.setState({ rejectReason: e.target.value })}
          placeholder={intl.formatMessage({
            id: 'request_details.moderation_message.reject_reason.placeholder',
          })}
          data-testid="reject-reason-text"
          onPressEnter={() => this.rejectTicket()}
          autosize={{ minRows: 3 }}
        />
      </Modal>
    )

    return (
      <div>
        {shouldShowTicketClaimedButton(requestTicket, user) && (
          <Button data-testid="btn-ticket-claimed" block disabled={true}>
            <FormattedMessage id="request_details.moderation_message.claimed" />{' '}
            <strong>{requestTicket.moderator!.name}</strong>
          </Button>
        )}
        {shouldShowModerationClaimButton(requestTicket) && (
          <Button
            block
            href="#"
            data-testid="btn-claim"
            loading={loading && loadingButton === ModerationButton.CLAIM}
            type="primary"
            onClick={async () => {
              this.setState({ loading: true, loadingButton: ModerationButton.CLAIM })
              try {
                await claimTicket({ variables: { uuid: requestTicket.uuid } })
                await refetchFunc()
              } catch (error) {
                this.setState({ loading: false })
              }
            }}
          >
            <span>
              <FormattedMessage id="request_details.moderation_button.claim" />
            </span>
          </Button>
        )}

        {shouldShowModerationPanel(requestTicket, user) && (
          <div>
            <FullWidthButtonGroup>
              <Button
                href="#"
                loading={loading && loadingButton === ModerationButton.APPROVE}
                icon="check"
                data-testid="btn-approve"
                onClick={async () => {
                  this.setState({ loading: true, loadingButton: ModerationButton.APPROVE })
                  try {
                    await approveTicket({ variables: { uuid: requestTicket.uuid } })
                    await refetchFunc()
                  } catch (error) {
                    this.setState({ loading: false })
                  }
                }}
                type="primary"
              >
                <span>
                  <FormattedMessage id="request_details.moderation_button.approve" />
                </span>
              </Button>

              <Button
                href="#"
                data-testid="btn-reject"
                loading={loading && loadingButton === ModerationButton.REJECT}
                type="danger"
                icon="stop"
                onClick={e => {
                  e.preventDefault()
                  this.setState({ modalOpen: true })
                }}
              >
                <span>
                  <FormattedMessage id="request_details.moderation_button.reject" />
                </span>
              </Button>
              <Button
                href="#"
                loading={loading && loadingButton === ModerationButton.DROP}
                type="ghost"
                icon="close"
                data-testid="btn-drop"
                onClick={async () => {
                  this.setState({ loading: true, loadingButton: ModerationButton.DROP })
                  try {
                    await dropTicket({ variables: { uuid: requestTicket.uuid } })
                    await refetchFunc()
                  } catch (error) {
                    this.setState({ loading: false })
                  }
                }}
              >
                <span>
                  <FormattedMessage id="request_details.moderation_button.drop" />
                </span>
              </Button>
            </FullWidthButtonGroup>
            {rejectModal}
          </div>
        )}
      </div>
    )
  }
}

export default compose(
  graphql(CLAIM_REQUEST_TICKET, { name: 'claimTicket' }),
  graphql(APPROVE_REQUEST_TICKET, { name: 'approveTicket' }),
  graphql(REJECT_REQUEST_TICKET, { name: 'rejectTicket' }),
  graphql(DROP_REQUEST_TICKET, { name: 'dropTicket' }),
  injectIntl
)(ModerationPanel)



// WEBPACK FOOTER //
// ./src/components/RequestDetailsPage/ModerationPanel/ModerationPanel.tsx